/**
 * RequireSignIn.tsx
 * 
 * A wrapper component that redirects unauthenticated users to the sign-in flow.
 * Used for pages that require user authentication via sign-in (not signup).
 * 
 * Usage:
 * <RequireSignIn redirectPath="/your/protected/path">
 *   <YourProtectedComponent />
 * </RequireSignIn>
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AlyzeLoadingSpinner } from '../../../componentsV1/shared';
import { makeStyles } from '@fluentui/react-components';

const useStyles = makeStyles({
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: '16px',
        padding: '24px',
    }
});

interface RequireSignInProps {
    /** The component(s) to render when authenticated */
    children: React.ReactNode;
    /** The path to redirect back to after sign-in completes */
    redirectPath: string;
}

export const RequireSignIn: React.FC<RequireSignInProps> = ({ children, redirectPath }) => {
    const auth = useAuth();
    const styles = useStyles();
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        // If not authenticated and not already loading/navigating, trigger sign-in
        if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !isRedirecting) {
            setIsRedirecting(true);
            // Store the redirect path
            sessionStorage.setItem('auth.redirectPath', redirectPath);
            auth.signinRedirect().catch((error) => {
                console.error("Sign-in redirect failed:", error);
                setIsRedirecting(false);
            });
        }
    }, [auth, redirectPath, isRedirecting]);

    // Show loading state while auth is initializing
    if (auth.isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Loading..." />
            </div>
        );
    }

    // Show redirecting state
    if (isRedirecting || auth.activeNavigator) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message="Redirecting to sign in..." />
            </div>
        );
    }

    // Show error state if there's an auth error
    if (auth.error) {
        return (
            <div className={styles.loadingContainer}>
                <AlyzeLoadingSpinner message={`Authentication error: ${auth.error.message}`} />
                <div>Please try again.</div>
            </div>
        );
    }

    // Only render children if authenticated
    if (auth.isAuthenticated) {
        return <>{children}</>;
    }

    // Default loading state (shouldn't normally reach here)
    return (
        <div className={styles.loadingContainer}>
            <AlyzeLoadingSpinner message="Authenticating..." />
        </div>
    );
};
